import { isAxiosError, isCancel } from 'axios';

export class OperationAbortedError extends Error {
  constructor(
    public reason: string,
    public error?: unknown,
  ) {
    super();
  }
}

type TApiErrorBody = {
  message?: string;
  code?: string;
};

function readApiErrorMessage(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return null;
  }

  const message = (data as TApiErrorBody).message;

  if (typeof message !== 'string' || message.length === 0) {
    return null;
  }

  return message;
}

export function getApiErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) {
    return null;
  }

  const data = error.response?.data;

  if (typeof data !== 'object' || data === null || !('code' in data)) {
    return null;
  }

  const code = (data as TApiErrorBody).code;

  if (typeof code !== 'string' || code.length === 0) {
    return null;
  }

  return code;
}

export function getApiErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (isAxiosError(error)) {
    const apiMessage = readApiErrorMessage(error.response?.data);

    if (apiMessage) {
      return apiMessage;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function throwApiError(e: unknown): never {
  if (e instanceof OperationAbortedError) {
    throw e;
  }

  if (isCancel(e)) {
    throw new OperationAbortedError(e.message ?? '', e);
  }

  if (e instanceof Error) {
    throw e;
  }

  throw new Error('Unknown error');
}
